// Slide-in detail drawer for a single user or sim. Reads `item.logs` for
// the inline activity log and exposes a "see full page" affordance that
// drills the shell into UserDetailPage / SimDetailPage.
//
// Postgres mapping: drawer is a lightweight summary; consumes the same
// `users` / `simulations` rows as the full detail pages.
// Integration hook: reuse `useUserDetailQuery` / `useSimDetailQuery`
// with a `{ summary: true }` flag for a lighter payload.
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';

export default function Drawer({ item, type, onClose, go }) {
    return (
        <AnimatePresence>
            {item && (
                <motion.div
                    className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-justify-end tw-z-[1300]"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
                >
                    <motion.div
                        className="tw-bg-[var(--surface)] tw-w-[400px] tw-max-w-[90vw] tw-h-full tw-border-l tw-border-[var(--border)] tw-p-4 tw-overflow-y-auto"
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    >
                        {(() => {
                            const sub =
                                type === 'sim'
                                    ? `${item.click} - ${item.diff === 3 ? 'Hard' : item.diff === 2 ? 'Medium' : 'Easy'}`
                                    : `${item.dept} - risk ${item.risk}`;
                            return (
                                <>
                                    <span
                                        className="tw-float-right tw-text-[13px] tw-cursor-pointer tw-text-[var(--muted)] hover:tw-text-[var(--text)] tw-transition-colors tw-duration-150"
                                        onClick={onClose}
                                    >
                                        &#10005;
                                    </span>
                                    <div className="tw-text-[13px] tw-font-semibold tw-text-[var(--text)] tw-mb-0.5 tw-pr-5">{item.name}</div>
                                    <div className="tw-text-[11px] tw-text-[var(--muted)] tw-mb-3">{sub}</div>
                                    <span className="tw-block tw-text-[10px] tw-font-semibold tw-tracking-[.07em] tw-uppercase tw-text-[var(--dim)] tw-mb-1.5">
                                        Raw event log
                                    </span>
                                    {item.logs.map((l, i) => (
                                        <div key={i} className="tw-text-[11px] tw-font-mono tw-text-[var(--muted)] tw-py-1.5 tw-border-b tw-border-[var(--border)] tw-flex tw-gap-2.5 last:tw-border-0">
                                            <span className="tw-text-[var(--text)] tw-font-semibold tw-min-w-[120px] tw-shrink-0">{l.k}</span>
                                            <span>{l.v}</span>
                                        </div>
                                    ))}
                                    <Button
                                        variant="teal"
                                        className="tw-mt-3.5 tw-w-full tw-justify-center"
                                        onClick={() => { onClose(); go('export-builder', 'Export builder'); }}
                                    >
                                        &#11015; Export this data
                                    </Button>
                                </>
                            );
                        })()}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
